import useAsync from 'react-use/lib/useAsync';
import { Link, Progress, ResponseErrorPanel } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import {
  isCountComparable,
  PACKAGE_FILTER_LABELS,
} from '@internal/plugin-scorecards-common';
import type {
  FactValue,
  FreshnessRow,
  UnresolvedReason,
  VulnerabilityRow,
} from '@internal/plugin-scorecards-common';
import { scorecardsApiRef } from '../api/ScorecardsApi';

const UNRESOLVED_TEXT: Record<UnresolvedReason, string> = {
  noVersion: 'no usable version',
  unknownPackage: 'unknown to the registries',
  versionNotListed: 'version not listed',
  noStableLatest: 'no stable release',
  notChecked: 'not checked yet',
};

const day = (iso?: string) => (iso ? iso.slice(0, 10) : '?');

function gapText(gapDays: number): string {
  const days = Math.round(gapDays);
  return days === 1 ? '1 day' : `${days} days`;
}

function flags(row: FreshnessRow): string {
  const found: string[] = [];
  if (row.unresolved) {
    found.push(UNRESOLVED_TEXT[row.unresolved] ?? row.unresolved);
  }
  if (row.outdated) {
    found.push('outdated');
  }
  if (row.majorBehind) {
    found.push('major behind');
  }
  if (row.deprecated) {
    found.push('deprecated');
  }
  return found.join(', ');
}

function FreshnessTable({
  rows,
  label,
}: {
  rows: FreshnessRow[];
  label: string;
}) {
  return (
    <Table size="small" aria-label={label}>
      <TableHead>
        <TableRow>
          <TableCell>Package</TableCell>
          <TableCell>Ecosystem</TableCell>
          <TableCell>Current</TableCell>
          <TableCell>Latest</TableCell>
          <TableCell>Published</TableCell>
          <TableCell align="right">Gap</TableCell>
          <TableCell>Status</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(row => (
          <TableRow key={`${row.ecosystem}/${row.name}`}>
            <TableCell>{row.name}</TableCell>
            <TableCell>{row.ecosystem}</TableCell>
            <TableCell>{row.current ?? ''}</TableCell>
            <TableCell>{row.latest ?? ''}</TableCell>
            <TableCell>
              {row.currentPublishedAt || row.latestPublishedAt
                ? `${day(row.currentPublishedAt)} → ${day(
                    row.latestPublishedAt,
                  )}`
                : ''}
            </TableCell>
            <TableCell align="right">
              {row.gapDays === undefined ? '' : gapText(row.gapDays)}
            </TableCell>
            <TableCell>{flags(row)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function VulnerabilityTable({
  rows,
  label,
}: {
  rows: VulnerabilityRow[];
  label: string;
}) {
  return (
    <Table size="small" aria-label={label}>
      <TableHead>
        <TableRow>
          <TableCell>Package</TableCell>
          <TableCell>Ecosystem</TableCell>
          <TableCell>Version</TableCell>
          <TableCell>Dependency</TableCell>
          <TableCell>Severity</TableCell>
          <TableCell>Fix available</TableCell>
          <TableCell>Advisories</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(row => (
          <TableRow key={`${row.ecosystem}/${row.name}@${row.version}`}>
            <TableCell>{row.name}</TableCell>
            <TableCell>{row.ecosystem}</TableCell>
            <TableCell>{row.version}</TableCell>
            <TableCell>{row.direct ? 'direct' : 'transitive'}</TableCell>
            <TableCell>{row.severity}</TableCell>
            <TableCell>{row.fixable ? 'yes' : 'no'}</TableCell>
            <TableCell>
              {row.advisories.map(advisory => (
                <div key={advisory.id}>
                  <Link
                    to={`https://osv.dev/vulnerability/${encodeURIComponent(
                      advisory.id,
                    )}`}
                  >
                    {advisory.id}
                  </Link>
                </div>
              ))}
              {row.detailsMissing && (
                <Typography variant="caption" color="textSecondary">
                  details unavailable for some advisories
                </Typography>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * The packages behind one package fact of a Component, loaded when it is
 * mounted. `ruleValue` is the fact's value the rule saw, to notice when the
 * stored data has moved on since.
 */
export function PackageList({
  entityRef,
  fact,
  ruleValue,
}: {
  entityRef: string;
  fact: string;
  ruleValue: FactValue | null | undefined;
}) {
  const api = useApi(scorecardsApiRef);
  // useAsync drops a superseded response, so a slow answer for another fact
  // never overwrites the current one.
  const { value, loading, error } = useAsync(
    () => api.getPackageList(entityRef, fact),
    [api, entityRef, fact],
  );

  if (loading) {
    return <Progress />;
  }
  if (error) {
    return <ResponseErrorPanel error={error} />;
  }
  if (!value) {
    return null;
  }
  if (value.scan === null) {
    return (
      <Typography variant="body2">
        Not scanned yet: this repository has not been scanned for packages.
      </Typography>
    );
  }

  const scannedAt = Date.parse(value.scan.scannedAt);
  const scanned = Number.isNaN(scannedAt)
    ? 'Scan time unknown'
    : `Scanned ${new Date(scannedAt).toLocaleString()}`;
  const tableLabel = `Packages for ${fact}`;
  const unchecked = value.view === 'vulnerabilities' ? value.unchecked : 0;
  const mismatch =
    isCountComparable(fact) &&
    typeof ruleValue === 'number' &&
    ruleValue !== value.count;

  return (
    <Box>
      <Typography variant="body2" color="textSecondary">
        {PACKAGE_FILTER_LABELS[value.filter]}: {value.count}. {scanned}
        {value.scan.complete ? '' : ', scan incomplete'}.
      </Typography>
      {mismatch && (
        <Typography variant="body2" color="textSecondary">
          The rule saw {ruleValue}; the stored data now lists {value.count} (the
          stored data changed since this scorecard was evaluated).
        </Typography>
      )}
      {value.truncated && (
        <Typography variant="body2" color="textSecondary">
          Showing the first {value.rows.length} of {value.count}.
        </Typography>
      )}
      {unchecked > 0 && (
        <Typography variant="body2" color="textSecondary">
          {unchecked === 1
            ? '1 package has no stored vulnerability data (not collected yet, or pruned), so it is not listed.'
            : `${unchecked} packages have no stored vulnerability data (not collected yet, or pruned), so they are not listed.`}
        </Typography>
      )}
      {value.rows.length === 0 && (
        <Typography variant="body2">
          {unchecked > 0
            ? 'No vulnerable packages are known yet.'
            : 'No packages match: the stored scan found none.'}
        </Typography>
      )}
      {value.rows.length > 0 && value.view === 'freshness' && (
        <FreshnessTable rows={value.rows} label={tableLabel} />
      )}
      {value.rows.length > 0 && value.view === 'vulnerabilities' && (
        <VulnerabilityTable rows={value.rows} label={tableLabel} />
      )}
    </Box>
  );
}
